import { cn } from "@/lib/utils";
import { LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";

type IconComponent = React.ComponentType<{ className?: string }>;

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    direction: "up" | "down" | "neutral";
  };
  icon?: LucideIcon | IconComponent;
  variant?: "default" | "primary" | "warning" | "success" | "info";
  /**
   * Quando fornecida, sobrescreve o variant com uma cor específica
   * (usada para casar as cores dos mini-charts no Dashboard).
   * Aceita qualquer valor CSS válido (ex.: "#ef4444", "hsl(...)").
   */
  color?: string;
  /**
   * Classes de background personalizadas (sobrescreve variant e color)
   */
  bgClassName?: string;
  /**
   * Classes de cor do texto personalizadas
   */
  textClassName?: string;
  className?: string;
}

const variantStyles = {
  default: "bg-card",
  primary: "bg-primary/5 border-primary/20",
  warning: "bg-warning/5 border-warning/20",
  success: "bg-success/5 border-success/20",
  info: "bg-info/5 border-info/20",
};

const iconVariantStyles = {
  default: "bg-surface-container text-foreground",
  primary: "bg-primary/10 text-primary",
  warning: "bg-warning/10 text-warning",
  success: "bg-success/10 text-success",
  info: "bg-info/10 text-info",
};

export function KPICard({
  title,
  value,
  subtitle,
  trend,
  icon: Icon,
  variant = "default",
  color,
  bgClassName,
  textClassName,
  className,
}: KPICardProps) {
  const TrendIcon =
    trend?.direction === "up"
      ? TrendingUp
      : trend?.direction === "down"
      ? TrendingDown
      : Minus;

  const trendColor =
    trend?.direction === "up"
      ? "text-success"
      : trend?.direction === "down"
      ? "text-destructive"
      : "text-muted-foreground";

  // Quando `bgClassName` é fornecida, usa as classes de background personalizadas
  const hasCustomBg = Boolean(bgClassName);
  const hasCustomText = Boolean(textClassName);

  // Quando `color` é fornecida, aplicamos estilos inline para casar com a cor
  // exata do mini-chart correspondente, sobrescrevendo o variant.
  const hasCustomColor = Boolean(color);
  const cardStyle = hasCustomColor
    ? {
        backgroundColor: `${color}14`, // ~8% alpha
        borderColor: `${color}33`, // ~20% alpha
      }
    : undefined;
  const iconStyle = hasCustomColor
    ? {
        backgroundColor: `${color}26`, // ~15% alpha
        color,
      }
    : undefined;

  return (
    <div
      className={cn(
        "surface-elevated p-5 animate-fade-in border",
        !hasCustomBg && !hasCustomColor && variantStyles[variant],
        hasCustomBg && bgClassName,
        className,
      )}
      style={cardStyle}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="kpi-label truncate">{title}</p>
          <p
            className={cn("kpi-value mt-1", hasCustomText && textClassName)}
            style={hasCustomColor && !hasCustomText ? { color } : undefined}
          >
            {value}
          </p>
          {subtitle && (
            <p className="text-caption mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className={cn("flex items-center gap-1 mt-2", trendColor)}>
              <TrendIcon className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">
                {trend.value > 0 ? "+" : ""}
                {trend.value}%
              </span>
              <span className="text-caption">vs. mês anterior</span>
            </div>
          )}
        </div>
        {Icon && (
          <div
            className={cn(
              "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0",
              !hasCustomBg && !hasCustomColor && iconVariantStyles[variant],
            )}
            style={iconStyle}
          >
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}
